/**
 * W-8IMY Service
 * Following the same clean architecture pattern as W8BEN, W8ECI, W8EXP, W8BENE, and W9 services
 */

import { W8IMYFormModel, W8IMYResponseModel, W8IMYFieldMapper } from "../Models/W8IMYModels";
import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { updateFileData } from "./fileDataService";

export class W8IMYService {
  private static readonly API_ENDPOINT = `${BASE_URL}/FileData/ImportFiles`;

  /**
   * Create form model from API response data
   * Note: API only sends selected checkboxes, unselected ones are not included
   */
  static fromApiResponse(apiData: any): W8IMYFormModel {
    // Create response model from API data
    const responseModel = new W8IMYResponseModel(apiData);

    // Map legacy API field names and convert boolean values
    this.mapLegacyApiFields(apiData, responseModel);

    // Use field mapper to convert to form model
    const formModel = W8IMYFieldMapper.mapResponseToForm(responseModel);

    // Handle checkbox behavior: API only includes selected checkboxes
    // Any checkbox field not present in API data should be false
    this.handleCheckboxDefaults(formModel, apiData);

    return formModel;
  }

  /**
   * Handle checkbox defaults - if a checkbox field isn't in API data, it should be false
   */
  private static handleCheckboxDefaults(formModel: W8IMYFormModel, apiData: any): void {
    const checkboxFields = [
      // Chapter 3 Status checkboxes (Part I)
      "IMY-4_QI",
      "IMY-4_Non_qualified_intermediary",
      "IMY-4_Territory_financial_institution",
      "IMY-4_US_branch",
      "IMY-4_Withholding_foreign_partnership",
      "IMY-4_Withholding_foreign_trust",
      "IMY-4_Non_withholding_foreign_partnership",
      "IMY-4_Non_withholding_foreign_simple_trust",
      "IMY-4_Non_withholding_foreign_grantor_trust",

      // Chapter 4 Status checkboxes (Section 5 from API)
      "IMY-5_non_participating_FFI",
      "IMY-5_participating_FFI",
      "IMY-5_reporting_model1_FFI",
      "IMY-5_reporting_model2_FFI",
      "IMY-5_registered_deemed_compliant_FFI",
      "IMY-5_territory_financial_institution",
      "IMY-5_sponsored_FFI",
      "IMY-5_certified_deemed_local_bank",
      "IMY-5_certified_deemed_low_value",
      "IMY-5_certified_deemed_investment_vehicle",
      "IMY-5_certified_deemed_debt_entity",
      "IMY-5_certain_investment_entities",
      "IMY-5_owner_documented_FFI",
      "IMY-5_restricted_distributor",
      "IMY-5_foreign_central_bank",
      "IMY-5_non_reporting_IGA_FFI",
      "IMY-5_exempt_retirement_plans",
      "IMY-5_excepted_non_financial_group",
      "IMY-5_excepted_non_financial_startup",
      "IMY-5_excepted_non_financial_liquidation",
      "IMY-5_publicly_traded_NFFE",
      "IMY-5_excepted_territory_NFFE",
      "IMY-5_active_NFFE",
      "IMY-5_passive_NFFE",
      "IMY-5_direct_reporting_NFFE",
      "IMY-5_sponsored_direct_reporting_NFFE",

      // TIN Type checkboxes
      "IMY-8_us_tin_qi_ein",
      "IMY-8_us_tin_wp_ein",
      "IMY-8_us_tin_wt_ein",
      "IMY-8_us_tin_ein",

      // Part II checkboxes
      "IMY-11_Branch_non_participating_FFI",
      "IMY-11_Participating_FFI",
      "IMY-11_Reporting_model1_FFI",
      "IMY-11_Reporting_model2_FFI",
      "IMY-11_US_Branch",

      // Part III QI Certification checkboxes
      "IMY-14_Certification_Qualified_Intermediary",
      "IMY-15A_primary_withholding_responsibility",
      "IMY-15B_publicly_traded_partnerships",
      "IMY-15C_withholding_as_nominee",
      "IMY-15D_qualified_securities_lender",
      "IMY-15E_withholding_and_form_1099_reporting",
      "IMY-15F_form_1099_reporting_for_us",
      "IMY-15G_non_assumption_of_form_1099_reporting",
      "IMY-15H_chapter4_withholding_rate_pool",
      "IMY-15I_intermediary_orflow_through_entities",
      "IMY-16A_certification_qdd_meets_requirements",
      "IMY-16B_entity_classification_corporation",
      "IMY-16B_entity_classification_partnership",
      "IMY-16B_entity_classification_disregarded_entity",

      // Part IV NQI checkboxes
      "IMY-17A_not_acting_as_qi",
      "IMY-17B_transmit_withholding_certificates",
      "IMY-17C_nqi_withholding_rate_pool",
      "IMY-17D_qualified_securities_lender",
      "IMY-17E_alternative_withholding_statement",

      // Part V TFI checkboxes
      "IMY-18A_tfi_certification",
      "IMY-18B_tfi_us_person_treatment",
      "IMY-18C_transmit_withholding_certificates",
      "IMY-18D_publicly_traded_partnerships",
      "IMY-18E_us_person_and_nominee",
      "IMY-18F_tfi_not_nominee",

      // Part VI US Branch checkboxes
      "IMY-19A_us_branch_certification",
      "IMY-19B_us_branch_agreement",
      "IMY-19C_transmit_certificates",
      "IMY-19D_us_branch_ptp",
      "IMY-19E_us_branch_nominee",
      "IMY-19F_us_branch_not_nominee",

      // Part VII WP/WT
      "IMY-20_withholding_foreign_partnership",

      // Part VIII Non-withholding entities
      "IMY-21A_foreign_partnership_trust",
      "IMY-21B_lower_tier_partnership",
      "IMY-21C_transfer_of_interest",
      "IMY-21D_statement_for_modified_amount",
      "IMY-21E_behalf_of_grantors",
      "IMY-21F_alternative_withholding_statement",

      // Part IX Nonparticipating FFI
      "IMY-22_exempt_beneficial_owners",

      // Part X Sponsored FFI
      "IMY-23B_sponsored_ffi_investment",
      "IMY-23C_controlled_foreign_corporation",

      // Part XI Owner documented FFI
      "IMY-24A_owner_documented_ffi",
      "IMY-24B_FFI_owner_reporting_statement",
      "IMY-24C_owner_documented_ffi_auditor",

      // Parts XII-XXVIII
      "IMY-25_nonregistering local bank _certification",
      "IMY-26_low_value_accounts",
      "IMY-27B_sponsored_investment_vehicle",
      "IMY-28_limited_life_debt",
      "IMY-29_financial_institution_solely",
      "IMY-30A_restricted_distributor",
      "IMY-30B_restricted_distributor_agreement",
      "IMY-30C_restricted_distributor_current",
      "IMY-31_central_bank",
      "IMY-32_nonreporting_iga_ffi",
      "IMY-32_model1_iga",
      "IMY-32_model2_iga",
      "IMY-32_trustee_us",
      "IMY-32_trustee_foreign",
      "IMY-33A_retirement_plan_treaty",
      "IMY-33B_retirement_plan_employer",
      "IMY-33C_retirement_plan_small",
      "IMY-33D_retirement_plan_401a",
      "IMY-33E_retirement_plan_investment",
      "IMY-33F_retirement_plan_government",
      "IMY-34_nonfinancial_group_entity",
      "IMY-35_startup_company_certification",
      "IMY-36_Liquidation_or_bankruptcy",
      "IMY-37A_publicly_traded_nffe",
      "IMY-37B_nffe_affiliate",
      "IMY-38_territory_nffe",
      "IMY-39_active_nffe",
      "IMY-40_passive_nffe",
      "IMY-42_sponsored_direct_reporting",

      // Other checkboxes
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    checkboxFields.forEach((field) => {
      // If the field is not present in API data, set it to false
      if (!(field in apiData)) {
        (formModel as any)[field] = false;
      } else {
        // If it is present, convert to boolean properly
        const value = apiData[field];
        const boolValue = this.convertToBoolean(value);
        (formModel as any)[field] = boolValue;
      }
    });
  }

  /**
   * Convert various value types to boolean for checkboxes
   */
  private static convertToBoolean(value: any): boolean {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      return (
        value.toLowerCase() === "true" ||
        value === "1" ||
        value.toLowerCase() === "yes" ||
        value.toLowerCase() === "selected"
      );
    }
    if (typeof value === "number") return value === 1;
    return !!value;
  }

  /**
   * Map legacy API field names to current field names
   */
  private static mapLegacyApiFields(apiData: any, responseModel: W8IMYResponseModel): void {
    const legacyMappings: Record<string, string[]> = {
      "Name of organization": [
        "Organization_name",
        "organization_name",
        "Name",
        "name",
        "entity_name",
      ],
      "Country of incorporation or organization": [
        "Country_of_incorporation",
        "incorporation_country",
        "Country_of_incorporation_organization",
      ],
      "Permanent residence address": [
        "Permanent_residence_address",
        "permanent_address",
        "address_line_1",
        "Permanent residence address",
      ],
      "Permanent city address": [
        "Permanent_city_address",
        "permanent_city",
        "city_state_zip",
        "Permanent city address",
      ],
      "Permanent address country": [
        "Permanent_address_country",
        "permanent_country",
        "country",
        "Permanent address country",
      ],
      "Mailing address": ["Mailing_address", "mailing_address", "Mailing address"],
      "Mailing address city": ["Mailing_address_city", "mailing_city", "Mailing address city"],
      "Mailing address country": [
        "Mailing_address_country",
        "mailing_country",
        "Mailing address country",
      ],
      "U.S. taxpayer identification number": [
        "US_taxpayer_identification_number",
        "us_tin",
        "U.S._taxpayer_identification_number",
        "IMY-8_us_tin",
      ],
      GIIN: ["GIIN", "giin", "global_intermediary_identification_number", "IMY-9A_giin"],
      "Foreign tax identifying number": [
        "Foreign_tax_identifying_number",
        "foreign_tin",
        "Foreign_tax_identifying_number",
        "IMY-9B_foreign_tax_identifying_number",
      ],
      "IMY-10_reference_number": [
        "IMY-10_reference_number",
        "reference_number",
        "Reference_number",
        "IMY-10_reference_number",
      ],
      GIINCheckBox: ["GIINCheckBox", "giin_checkbox", "giin_check"],
      "GIIN Applied For": ["GIIN Applied For", "giin_applied_for", "giin_applied"],
      "Foreign TIN": ["Foreign TIN", "foreign_tin_checkbox", "foreign_tin_check"],

      // TIN Type mappings
      "IMY-8_us_tin_qi_ein": ["IMY-8_us_tin_qi_ein", "us_tin_qi_ein"],
      "IMY-8_us_tin_wp_ein": ["IMY-8_us_tin_wp_ein", "us_tin_wp_ein"],
      "IMY-8_us_tin_wt_ein": ["IMY-8_us_tin_wt_ein", "us_tin_wt_ein"],
      "IMY-8_us_tin_ein": ["IMY-8_us_tin_ein", "us_tin_ein"],

      // Disregarded entity/branch mappings

      "IMY-12_disregarded_entity_address_of_branch": [
        "IMY-12_disregarded_entity_address_of_branch",
        "branch_address",
      ],
      "IMY-12_disregarded_entity_city_state_of_branch": [
        "IMY-12_disregarded_entity_city_state_of_branch",
        "branch_city_state",
      ],
      "IMY-12_disregarded_entity_country_of_branch": [
        "IMY-12_disregarded_entity_country_of_branch",
        "branch_country",
      ],
      "IMY-13_disregarded_entity_giin": ["IMY-13_disregarded_entity_giin", "branch_giin"],

      // Sponsoring entity names
      "IMY-23A_name_of_sponsoring_entity": [
        "IMY-23A_name_of_sponsoring_entity",
        "sponsoring_entity_name",
      ],
      "IMY-27A_sponsored_investment_vehicle_name": [
        "IMY-27A_sponsored_investment_vehicle_name",
        "investment_vehicle_name",
      ],
      "IMY-32_treated_as": ["IMY-32_treated_as", "treated_as"],
      "IMY-35_startup_formation_date": ["IMY-35_startup_formation_date", "formation_date"],
      "IMY-36_liquidation_filing_date": ["IMY-36_liquidation_filing_date", "filing_date"],

      // Part XXIV - Publicly Traded NFFE fields
      "IMY-37A_Textfield": ["IMY-37A_Textfield", "IMY-37A_securities_market", "securities_market"],
      "IMY-37B_Textfield": [
        "IMY-37B_Textfield",
        "IMY-37B_affiliate_entity_name",
        "affiliate_entity_name",
      ],
      "IMY-37B_affiliate_securities_market": [
        "IMY-37B_affiliate_securities_market",
        "affiliate_securities_market",
      ],

      "IMY-41_sponsor_name": ["IMY-41_sponsor_name", "sponsor_name"],

      // Chapter 3 Status fields
      "IMY-11_qualified_intermediary": ["IMY-11_qualified_intermediary", "qualified_intermediary"],
      "IMY-11_qualified_derivatives_dealer": [
        "IMY-11_qualified_derivatives_dealer",
        "qualified_derivatives_dealer",
      ],
      "IMY-11_withholding_foreign_partnership": [
        "IMY-11_withholding_foreign_partnership",
        "withholding_foreign_partnership",
      ],
      "IMY-11_withholding_foreign_trust": [
        "IMY-11_withholding_foreign_trust",
        "withholding_foreign_trust",
      ],
      "IMY-11_foreign_person": ["IMY-11_foreign_person", "foreign_person"],

      // Chapter 4 Status fields
      "IMY-12_nonparticipating_ffi": ["IMY-12_nonparticipating_ffi", "nonparticipating_ffi"],
      "IMY-12_participating_ffi": ["IMY-12_participating_ffi", "participating_ffi"],
      "IMY-12_reporting_model1_ffi": ["IMY-12_reporting_model1_ffi", "reporting_model1"],
      "IMY-12_reporting_model2_ffi": ["IMY-12_reporting_model2_ffi", "reporting_model2"],
      "IMY-12_us_branch": ["IMY-12_us_branch", "us_branch"],

      // QI Certification fields
      "IMY-14_Certification_Qualified_Intermediary": [
        "IMY-14_Certification_Qualified_Intermediary",
        "qi_certification",
      ],
      "IMY-15A_Primary_withholding_responsibility": [
        "IMY-15A_Primary_withholding_responsibility",
        "primary_withholding",
      ],
      "IMY-15B_Publicly_traded_partnerships": [
        "IMY-15B_Publicly_traded_partnerships",
        "publicly_traded_partnerships",
      ],
      "IMY-15C_Withholding_as_nominee": ["IMY-15C_Withholding_as_nominee", "withholding_nominee"],
      "IMY-15D_Qualified_securities_lender": [
        "IMY-15D_Qualified_securities_lender",
        "securities_lender",
      ],
      "IMY-15E_Withholding_and_Form_1099_reporting": [
        "IMY-15E_Withholding_and_Form_1099_reporting",
        "form_1099_reporting",
      ],
      "IMY-15F_Form_1099_reporting_for_US": ["IMY-15F_Form_1099_reporting_for_US", "form_1099_us"],
      "IMY-15G_Non-assumption+_of_Form_1099_reporting": [
        "IMY-15G_Non-assumption+_of_Form_1099_reporting",
        "non_assumption_1099",
      ],
      "IMY-15H-Chapter4_withholding_rate_pool": [
        "IMY-15H-Chapter4_withholding_rate_pool",
        "chapter4_withholding",
      ],
      "IMY-15I_Intermediary_orflow-through_entities": [
        "IMY-15I_Intermediary_orflow-through_entities",
        "intermediary_flow_through",
      ],

      // QDD Certification fields
      "IMY-16A_Certification_QDD_meets_requirements": [
        "IMY-16A_Certification_QDD_meets_requirements",
        "qdd_certification",
      ],
      "IMY-16B_Entity_classification_corporation": [
        "IMY-16B_Entity_classification_corporation",
        "entity_corporation",
      ],
      "IMY-16B_Entity_classification_partnership": [
        "IMY-16B_Entity_classification_partnership",
        "entity_partnership",
      ],
      "IMY-16B_Entity_classification_disregarded_entity": [
        "IMY-16B_Entity_classification_disregarded_entity",
        "entity_disregarded",
      ],

      // Signature fields
      Signature_date: ["Signature_date"],

      Print_name: ["Print_name", "printed_name", "signer_name", "Signer_printed_name"],
      Signing_capacity: ["Signing_capacity", "signing_capacity", "capacity"],
      certify: ["certify", "certification", "is_certified"],
      isSigned: ["isSigned", "is_signed", "signed"],
      isModified: ["isModified", "is_modified", "modified"],
      Form_name: ["Form_name", "form_name", "form_type"],
      Form_revision: ["Form_revision", "form_revision", "revision_date"],
    };

    // Apply legacy mappings
    Object.entries(legacyMappings).forEach(([currentField, legacyFields]) => {
      for (const legacyField of legacyFields) {
        if (apiData[legacyField] !== undefined) {
          responseModel[currentField] = apiData[legacyField];
          break;
        }
      }
    });

    // Convert string boolean values to actual booleans for checkbox fields
    const checkboxFields = [
      "certify",
      // TIN Type fields
      "IMY-US_TIN_EIN",
      "IMY-US_TIN_QI-EIN",
      "IMY-US_TIN_WP-EIN",
      "IMY-US_TIN_WT-EIN",
      // Part II Chapter 3 Status
      "IMY-4_QI",
      "IMY-4_Non_qualified_intermediary",
      "IMY-4_Withholding_foreign_partnership",
      "IMY-4_Withholding_foreign_trust",
      "IMY-4_Non_withholding_foreign_partnership",
      "IMY-4_Non_withholding_foreign_simple_trust",
      "IMY-4_Non_withholding_foreign_grantor_trust",
      "IMY-4_Territory_financial_institution",
      "IMY-4_US_branch",
      // Part III Chapter 4 Status
      "IMY-11_Branch_non_participating_FFI",
      "IMY-11_Participating_FFI",
      "IMY-11_Reporting_model1_FFI",
      "IMY-11_Reporting_model2_FFI",
      "IMY-11_US_Branch",
      // QI and QDD Certification fields
      "IMY-14_Certification_Qualified_Intermediary",
      "IMY-15A_Primary_withholding_responsibility",
      "IMY-15B_Publicly_traded_partnerships",
      "IMY-15C_Withholding_as_nominee",
      "IMY-15D_Qualified_securities_lender",
      "IMY-15E_Withholding_and_Form_1099_reporting",
      "IMY-15F_Form_1099_reporting_for_US",
      "IMY-15G_Non-assumption+_of_Form_1099_reporting",
      "IMY-15H-Chapter4_withholding_rate_pool",
      "IMY-15I_Intermediary_orflow-through_entities",
      "IMY-16A_Certification_QDD_meets_requirements",
      "IMY-16B_Entity_classification_corporation",
      "IMY-16B_Entity_classification_partnership",
      "IMY-16B_Entity_classification_disregarded_entity",
      // All additional certification fields
      "IMY-17A_not_acting_as_QI",
      "IMY-17B_Transmit_withholding_certificates",
      "IMY-17C_Regulations_section_1.6049-4(c)(4)(iii)",
      "IMY-17D_Qualified_securities_lender",
      "IMY-17E_Alternative_withholding_statement",
      "IMY-18A_Financial_institution_in_territory",
      "IMY-18B_Withholding_agent_to_be_treated_as_US_person",
      "IMY-18C_Transmit_withholding_certificates",
      "IMY-18D_Publicly_traded_partnerships",
      "IMY-18E_US_person_and_nominee",
      "IMY-18F_Nominee_and_providing_withholding_statements",
      "IMY-19A_Reportable_amounts_not_effectively_connected",
      "IMY-19B_Foreign_bank_or_insurance_company",
      "IMY-19C_Transmit_certificates",
      "IMY-19D_US_person_for_sales_of_interests",
      "IMY-19E_Entity_is_US_branch",
      "IMY-19F_Not_acting_as_a_nominee",
      "IMY-20_Withholding_foreign_partnership",
      "IMY-21A_Foreign_partnership/trust",
      "IMY-21B_Lower-tier_partnership",
      "IMY-21C_Transfer_of_interest",
      "IMY-21D_Statement_for_modified_amount",
      "IMY-21E_Behalf_of_grantors",
      "IMY-21F_Alternative_withholding_statement",
      "IMY-22_Exempt_beneficial_owners",
      "IMY-23B_QI/WP/WT",
      "IMY-23C_Controlled_foreign_corporation",
      "IMY-24A_Owner-documented_FFI_requirements",
      "IMY-24B_FFI_owner_reporting_statement",
      "IMY-24C_Auditor's_letter",
      "IMY-25_Non_registering_local_bank",
      "IMY-26_FFI_with_only_low_value_accounts",
      "IMY-27B_FFI_solely",
      "IMY-28_Life_debt_investment",
      "IMY-29_Financial_institution_solely",
      "IMY-30A_Restricted_distributor",
      "IMY-30B_Prohibition_on_US_sales",
      "IMY-30C_Agreement_with_specific_prohibitions",
      "IMY-31_Regulations_section_1.1471-6(d)(4)",
      "IMY-32_Model1_IGA",
      "IMY-32_Model2_IGA",
      "IMY-32_Non_reporting_financial_institution",
      "IMY-32_Trustee_foreign",
      "IMY-32_Trustee_us",
      "IMY-33A_US_tax_treaty",
      "IMY-33B_Former_employees",
      "IMY-33C_50_Participants",
      "IMY-33D_Section_401(a)",
      "IMY-33E_Retirement_funds",
      "IMY-33F_Foreign_government_or_international_organization",
      "IMY-34_Nonfinancial_group_entity",
      "IMY-35_Start_up_company",
      "IMY-36_Liquidation_or_bankruptcy",
      "IMY-37A_Stock_traded",
      "IMY-37B_Affiliated_group",
      "IMY-38_Bona_fide_residents",
      "IMY-39_Passive_income_and_assets",
      "IMY-40_Foreign_entity",
      "IMY-42_Line_41",
    ];

    checkboxFields.forEach((field) => {
      if (responseModel[field] !== undefined) {
        const value = responseModel[field];
        if (typeof value === "string") {
          responseModel[field] =
            value.toLowerCase() === "true" ||
            value === "1" ||
            value.toLowerCase() === "yes" ||
            value.toLowerCase() === "selected";
        }
      }
    });
  }

  /**
   * Convert form model to API payload format
   * 🔧 CRITICAL FIX: Send ALL checkbox fields (both true AND false values) to ensure unchecked boxes are saved properly
   */
  static toApiPayload(model: W8IMYFormModel): Record<string, any> {
    const payload: Record<string, any> = {};

    const checkboxFields = [
      // Chapter 3 Status checkboxes (Part I)
      "IMY-4_QI",
      "IMY-4_Non_qualified_intermediary",
      "IMY-4_Territory_financial_institution",
      "IMY-4_US_branch",
      "IMY-4_Withholding_foreign_partnership",
      "IMY-4_Withholding_foreign_trust",
      "IMY-4_Non_withholding_foreign_partnership",
      "IMY-4_Non_withholding_foreign_simple_trust",
      "IMY-4_Non_withholding_foreign_grantor_trust",

      // Chapter 4 Status checkboxes (Section 5 from API)
      "IMY-5_non_participating_FFI",
      "IMY-5_participating_FFI",
      "IMY-5_reporting_model1_FFI",
      "IMY-5_reporting_model2_FFI",
      "IMY-5_registered_deemed_compliant_FFI",
      "IMY-5_territory_financial_institution",
      "IMY-5_sponsored_FFI",
      "IMY-5_certified_deemed_local_bank",
      "IMY-5_certified_deemed_low_value",
      "IMY-5_certified_deemed_investment_vehicle",
      "IMY-5_certified_deemed_debt_entity",
      "IMY-5_certain_investment_entities",
      "IMY-5_owner_documented_FFI",
      "IMY-5_restricted_distributor",
      "IMY-5_foreign_central_bank",
      "IMY-5_non_reporting_IGA_FFI",
      "IMY-5_exempt_retirement_plans",
      "IMY-5_excepted_non_financial_group",
      "IMY-5_excepted_non_financial_startup",
      "IMY-5_excepted_non_financial_liquidation",
      "IMY-5_publicly_traded_NFFE",
      "IMY-5_excepted_territory_NFFE",
      "IMY-5_active_NFFE",
      "IMY-5_passive_NFFE",
      "IMY-5_direct_reporting_NFFE",
      "IMY-5_sponsored_direct_reporting_NFFE",

      // TIN Type checkboxes
      "IMY-8_us_tin_qi_ein",
      "IMY-8_us_tin_wp_ein",
      "IMY-8_us_tin_wt_ein",
      "IMY-8_us_tin_ein",

      // Part II checkboxes
      "IMY-11_Branch_non_participating_FFI",
      "IMY-11_Participating_FFI",
      "IMY-11_Reporting_model1_FFI",
      "IMY-11_Reporting_model2_FFI",
      "IMY-11_US_Branch",

      // Part III QI Certification checkboxes
      "IMY-14_Certification_Qualified_Intermediary",
      "IMY-15A_primary_withholding_responsibility",
      "IMY-15B_publicly_traded_partnerships",
      "IMY-15C_withholding_as_nominee",
      "IMY-15D_qualified_securities_lender",
      "IMY-15E_withholding_and_form_1099_reporting",
      "IMY-15F_form_1099_reporting_for_us",
      "IMY-15G_non_assumption_of_form_1099_reporting",
      "IMY-15H_chapter4_withholding_rate_pool",
      "IMY-15I_intermediary_orflow_through_entities",
      "IMY-16A_certification_qdd_meets_requirements",
      "IMY-16B_entity_classification_corporation",
      "IMY-16B_entity_classification_partnership",
      "IMY-16B_entity_classification_disregarded_entity",

      // Part IV NQI checkboxes
      "IMY-17A_not_acting_as_qi",
      "IMY-17B_transmit_withholding_certificates",
      "IMY-17C_nqi_withholding_rate_pool",
      "IMY-17D_qualified_securities_lender",
      "IMY-17E_alternative_withholding_statement",

      // Part V TFI checkboxes
      "IMY-18A_tfi_certification",
      "IMY-18B_tfi_us_person_treatment",
      "IMY-18C_transmit_withholding_certificates",
      "IMY-18D_publicly_traded_partnerships",
      "IMY-18E_us_person_and_nominee",
      "IMY-18F_tfi_not_nominee",

      // Part VI US Branch checkboxes
      "IMY-19A_us_branch_certification",
      "IMY-19B_us_branch_agreement",
      "IMY-19C_transmit_certificates",
      "IMY-19D_us_branch_ptp",
      "IMY-19E_us_branch_nominee",
      "IMY-19F_us_branch_not_nominee",

      // Part VII WP/WT
      "IMY-20_withholding_foreign_partnership",

      // Part VIII Non-withholding entities
      "IMY-21A_foreign_partnership_trust",
      "IMY-21B_lower_tier_partnership",
      "IMY-21C_transfer_of_interest",
      "IMY-21D_statement_for_modified_amount",
      "IMY-21E_behalf_of_grantors",
      "IMY-21F_alternative_withholding_statement",

      // Part IX Nonparticipating FFI
      "IMY-22_exempt_beneficial_owners",

      // Part X Sponsored FFI
      "IMY-23B_sponsored_ffi_investment",
      "IMY-23C_controlled_foreign_corporation",

      // Part XI Owner documented FFI
      "IMY-24A_owner_documented_ffi",
      "IMY-24B_FFI_owner_reporting_statement",
      "IMY-24C_owner_documented_ffi_auditor",

      // Parts XII-XXVIII
      "IMY-25_nonregistering local bank _certification",
      "IMY-26_low_value_accounts",
      "IMY-27B_sponsored_investment_vehicle",
      "IMY-28_limited_life_debt",
      "IMY-29_financial_institution_solely",
      "IMY-30A_restricted_distributor",
      "IMY-30B_restricted_distributor_agreement",
      "IMY-30C_restricted_distributor_current",
      "IMY-31_central_bank",
      "IMY-32_nonreporting_iga_ffi",
      "IMY-32_model1_iga",
      "IMY-32_model2_iga",
      "IMY-32_trustee_us",
      "IMY-32_trustee_foreign",
      "IMY-33A_retirement_plan_treaty",
      "IMY-33B_retirement_plan_employer",
      "IMY-33C_retirement_plan_small",
      "IMY-33D_retirement_plan_401a",
      "IMY-33E_retirement_plan_investment",
      "IMY-33F_retirement_plan_government",
      "IMY-34_nonfinancial_group_entity",
      "IMY-35_startup_company_certification",
      "IMY-36_Liquidation_or_bankruptcy",
      "IMY-37A_publicly_traded_nffe",
      "IMY-37B_nffe_affiliate",
      "IMY-38_territory_nffe",
      "IMY-39_active_nffe",
      "IMY-40_passive_nffe",
      "IMY-42_sponsored_direct_reporting",

      // Other checkboxes
      "GIINCheckBox",
      "GIIN Applied For",
      "Foreign TIN",
    ];

    // Map all model fields to API payload
    Object.keys(model).forEach((field) => {
      const value = (model as any)[field];
      if (typeof value !== "function") {
        // For checkbox fields, only include them if they are true
        if (checkboxFields.includes(field)) {
          // 🔧 CRITICAL FIX: Always include checkbox values (both true AND false) in API payload
          // This ensures unchecked checkboxes are properly saved as false
          payload[field] = value === true ? true : false;
        } else {
          // For non-checkbox fields, include all defined values
          if (value !== undefined) {
            payload[field] = value;
          }
        }
      }
    });

    return payload;
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: W8IMYFormModel,
    row: any,
    selectedIndex: number = 0
  ): Promise<boolean> {
    try {
      // Enhanced authentication with multiple fallbacks
      let userData;
      let token;

      try {
        userData = getUserIdFromLocalStorage();
        token = userData?.token;
      } catch (error) {
        console.log("W8IMY Auth - getUserIdFromLocalStorage failed:", error);
      }

      // Fallback authentication methods
      if (!token) {
        try {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
            const parsed = JSON.parse(storedUserData);
            token = parsed.token;
          }
        } catch (error) {
          console.log("W8IMY Auth - fallback userData parsing failed:", error);
        }
      }

      if (!token) {
        try {
          token = localStorage.getItem("token");
        } catch (error) {
          console.log("W8IMY Auth - direct token retrieval failed:", error);
        }
      }

      if (!token) {
        console.error("W8IMY Auth - No authentication token found after all fallbacks");
        throw new Error("No authentication token found");
      }

      // Convert model to API payload
      const formData = this.toApiPayload(model);

      formData["Entity Type"] = "";
      delete formData.giinValidationMessage;
      delete formData.tinValidationMessage;
      let parsedFormsData;
      let isComplexStructure = false;
      try {
        parsedFormsData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedFormsData &&
          typeof parsedFormsData === "object" &&
          parsedFormsData.formsData &&
          Array.isArray(parsedFormsData.formsData)
        ) {
          isComplexStructure = true;
        } else if (Array.isArray(parsedFormsData)) {
          isComplexStructure = false;
        } else {
          parsedFormsData = [];
          isComplexStructure = false;
        }
      } catch (error) {
        parsedFormsData = [];
        isComplexStructure = false;
      }

      if (isComplexStructure) {
        // Find the correct form in the complex structure
        let formUpdated = false;

        for (let i = 0; i < parsedFormsData.formsData.length; i++) {
          const formItem = parsedFormsData.formsData[i];
          if (formItem && formItem.data && Array.isArray(formItem.data)) {
            // Check if this form contains the data we're looking for (by FormType or index)
            for (let j = 0; j < formItem.data.length; j++) {
              const dataItem = formItem.data[j];
              if (dataItem && (dataItem.formType === "W8IMY" || dataItem.Form_name === "W-8IMY")) {
                // Update this form's data
                parsedFormsData.formsData[i].data[j] = {
                  ...dataItem,
                  ...formData,
                };
                formUpdated = true;
                break;
              }
            }
            if (formUpdated) break;
          }
        }

        if (!formUpdated) {
          // Add new form structure if no existing W8IMY found
          parsedFormsData.formsData.push({
            request_id: "new-form-" + Date.now(),
            status: "success",
            data: [formData],
            reportdata: {
              requestId: "new-form-" + Date.now(),
              subRequestId: "new-form-" + Date.now(),
              taxForms: "W8IMY",
              type: "extraction",
            },
          });
        }

        // Preserve the entire complex structure including withholdingData
      } else {
        const formsDataArray = parsedFormsData;

        const currentItem = formsDataArray[selectedIndex];

        if (selectedIndex < formsDataArray.length && currentItem) {
          // Update existing form data by merging with new data
          formsDataArray[selectedIndex] = {
            ...currentItem,
            ...formData,
          };
        } else {
          // Ensure array is large enough
          while (formsDataArray.length <= selectedIndex) {
            formsDataArray.push({});
          }
          // Add new form data
          formsDataArray[selectedIndex] = formData;
        }

        parsedFormsData = formsDataArray;
      }

      // Store back as stringified structure (preserving complex format if applicable)
      row.formsData = JSON.stringify(parsedFormsData);

      // Send to API using centralized service
      await updateFileData(row);

      console.log("W8IMY Save operation completed successfully");
      return true;
    } catch (error) {
      console.error("Failed to save W-8IMY form:", error);
      throw error;
    }
  }
}
